import { Injectable, inject } from '@angular/core';
import { AuthService as Auth0Service, GetTokenSilentlyOptions } from '@auth0/auth0-angular';
import { Observable, map, BehaviorSubject, switchMap, of, distinctUntilChanged } from 'rxjs';
import { AUTH_CONFIG } from '../auth.config';
import { ContextService } from './context.service';

export interface User {
  userId?: string;
  sub?: string;
  name?: string;
  email?: string;
  picture?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth0 = inject(Auth0Service);
  private config = inject(AUTH_CONFIG);
  private contextService = inject(ContextService);
  private userSubject = new BehaviorSubject<User | null>(null);

  // synchronous snapshot cache
  private isAuthenticatedValue = false;

  constructor() {
    // Cache auth state synchronously for guards
    this.auth0.isAuthenticated$.subscribe(isAuth => {
      this.isAuthenticatedValue = isAuth;
    });

    // Existing user mapping
    this.auth0.user$.pipe(
      switchMap(auth0User => {
        if (!auth0User) {
          this.userSubject.next(null);
          return of(null);
        }
        
        const namespace = 'https://lrx';
        const user: User = {
          userId: auth0User[`${namespace}/userId`] || auth0User.sub,
          sub: auth0User.sub,
          name: auth0User[`${namespace}/name`] || auth0User.name || auth0User.email,
          email: auth0User.email,
          picture: auth0User.picture
        };
        
        return of(user);
      })
    ).subscribe(user => {
      this.userSubject.next(user);
    });
  }

  // synchronous getter for canMatch
  get isAuthenticatedSnapshot(): boolean {
    return this.isAuthenticatedValue;
  }

  user$: Observable<User | null | undefined> = this.userSubject.asObservable();

  isAuthenticated$: Observable<boolean> = this.auth0.isAuthenticated$.pipe(
    distinctUntilChanged()
  );

  isLoading$: Observable<boolean> = this.auth0.isLoading$;

  login(): void {
    this.auth0.loginWithRedirect({
      appState: { target: '/callback' },
      authorizationParams: {
        ...(this.config.connection && { connection: this.config.connection })
      }
    });
  }

  logout(): void {
    this.contextService.reset();
    this.userSubject.next(null);
    // Prod/test: app is at root (e.g. https://client.test.legalresources.com). Local: app is under /client (e.g. https://localhost:4300/client)
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const portalKey = (this.config.portalKey ?? 'client').toLowerCase();
    const returnTo = isLocal ? `${window.location.origin}/${portalKey}` : window.location.origin;
    this.auth0.logout({
      logoutParams: {
        returnTo
      }
    });
  }

  handleRedirectCallback(): Observable<any> {
    return this.auth0.handleRedirectCallback();
  }

  getAccessTokenSilently(options?: GetTokenSilentlyOptions): Observable<string> {
    return this.auth0.getAccessTokenSilently(options);
  }
}
